import { Subscription } from 'rxjs';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BlogEntry } from '../../models/blog-models.model';
import { BlogService } from '../../services/blog/blog.service';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-single-blog-page',
  templateUrl: './single-blog-page.component.html',
  styleUrl: './single-blog-page.component.scss'
})
export class SingleBlogPageComponent {
  private readonly siteUrl = environment.SITE_URL;

  public isLoading: boolean = true;
  public post!: BlogEntry;
  public topThreePosts: any;
  public nextPost: any;
  public prevPost: any;
  public id = '';
  public model = '';
  public actualPage = "";

  public posts: Array<any> = []
  public postsFiltered: Array<any> = []
  public index: number = 0;
  public last: number = 0;
  public loader: boolean = true;

  private postsSubscription!: Subscription;
  private routeSubscription!: Subscription;

  filteredCountries!: any[];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private blogService: BlogService,
    private titleService: Title, private metaService: Meta,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit(): void {
    this.loader = true;

    this.postsSubscription = this.blogService.getBlogEntries().subscribe({
      next: (entries) => {
        this.posts = entries;
        this.postsFiltered = entries;
        this.last = this.posts.length;

        this.routeSubscription = this.route.paramMap.subscribe((params) => {
          const postId = params.get('id');
          const postName = params.get('name');
          this.findPostByOrder(postId, postName);
          this.setMetaTags();
        });

        this.loader = false;
      },
      error: (error) => {
        this.loader = false;
      }
    });
  }

  setMetaTags() {
    if (this.post) {
      const metaTitle = this.post.metaTitle || this.post.blogTitle;
      const metaDescription = this.post.metaDescription || this.getDesc(100, this.post.blogContent);
      const name = this.post.blogTitle.toLocaleLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')
      const url = `${this.siteUrl}/blog/${name}/${this.post._id}`;
      const image = this.toAbsoluteUrl(this.getImageSrc(this.post));

      this.titleService.setTitle(`Paredes y Asociados Blog | ${metaTitle}`);
      this.metaService.updateTag({ name: 'description', content: metaDescription });

      this.metaService.updateTag({ name: "robots", content: "index, follow" });
      this.metaService.updateTag({ name: "description", content: metaDescription });
      this.setCanonicalUrl(url);

      this.metaService.updateTag({ property: "og:type", content: "article" });
      this.metaService.updateTag({ property: "og:locale", content: "es_MX" });
      this.metaService.updateTag({ property: "og:site_name", content: "Paredes y Asociados" });

      this.metaService.updateTag({ property: "og:title", content: `Paredes y Asociados Blog | ${metaTitle}` });
      this.metaService.updateTag({ property: "og:description", content: metaDescription });
      this.metaService.updateTag({ property: "og:url", content: url });
      this.metaService.updateTag({ property: 'og:image', content: image });
      this.metaService.updateTag({ property: 'og:image:alt', content: this.post.blogTitle });

      this.metaService.updateTag({ name: "twitter:card", content: "summary_large_image" });
      this.metaService.updateTag({ name: "twitter:title", content: "Paredes y Asociados Blog | " + metaTitle });
      this.metaService.updateTag({ name: "twitter:description", content: metaDescription });
      this.metaService.updateTag({ name: "twitter:url", content: url });
      this.metaService.updateTag({ name: "twitter:image", content: image });
    }
  }

  private setCanonicalUrl(url: string): void {
    const head = this.document.head;
    let link = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private toAbsoluteUrl(path: string): string {
    if (!path) {
      return `${this.siteUrl}/assets/images/logos/logo-full.webp`;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return `${this.siteUrl}${path}`;
  }

  ngOnDestroy(): void {
    if (this.postsSubscription) {
      this.postsSubscription.unsubscribe();
    }
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  getDesc(number: number, text: string) {
    const limiteCaracteres = number;
    if (text.length > limiteCaracteres) {
      return text.slice(0, limiteCaracteres) + "...";
    }
    return text;
  }

  async findPostByOrder(id: any, name: any) {
    try {
      let individualPost = this.posts.filter((entry: any) => entry._id == id);
      let namePost = this.posts.filter((entry: any) => entry.blogTitle == name);
      this.index = this.posts.findIndex((entry: any) => entry._id == id);
      this.topThreePosts = this.posts
        .slice(0, 3)
        .filter((entry: any) => entry._id !== id);
      if (individualPost.length == 0 && namePost.length == 0) {
        this.router.navigate(['/404']);
        return;
      }
      this.post = individualPost[0];
      let last = this.posts.length;
      const filteredPosts = this.posts.filter((entry: any) => entry._id !== id);
      this.topThreePosts = filteredPosts.slice(0, 3);
      if (this.index + 1 < this.posts.length) {
        this.nextPost = this.posts[this.index + 1];
      } else {
        this.nextPost = this.posts[0];
      }
      if (this.index - 1 >= 0) {
        this.prevPost = this.posts[this.index - 1];
      } else {
        this.prevPost = this.posts[last - 1];
      }
      this.isLoading = false;
    } catch (error) {
      this.router.navigate(['/blogs']);
    }
  }

  goEntry(id: any) {
    this.router.navigateByUrl(`/blog/${id}`);
    this.goTo('nav');
  }

  nextEntry() {
    const name = this.nextPost.blogTitle.toLocaleLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
    this.router.navigateByUrl(`/blog/${name}/${this.nextPost._id}`);
    this.goTo('nav');
  }

  prevEntry() {
    const name = this.prevPost.blogTitle.toLocaleLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
    this.router.navigateByUrl(`/blog/${name}/${this.prevPost._id}`);
    this.goTo('nav');
  }

  goTo(path: any, event?: any) {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        let element = document.querySelector('#' + path) as HTMLElement;
        let topOfElement = element.offsetTop - 100;
        window.scroll({ top: topOfElement, behavior: 'smooth' });
      }, 200);
    }
  }

  getContent(info: any, num: number) {
    let text = info;
    const limiteCaracteres = num;
    if (text.length > limiteCaracteres) {
      return text.slice(0, limiteCaracteres) + "...";
    }
    return text.replace(/<\/?p>/g, '');
  }


  formatDateToShort(inputDate: string): string {
    const months: string[] = [
      "Ene.", "Feb.", "Mar.", "Abr.", "May.", "Jun.",
      "Jul.", "Ago.", "Sep.", "Oct.", "Nov.", "Dic."
    ];
    const monthIndexMap: { [key: string]: number } = {
      "enero": 0,
      "febrero": 1,
      "marzo": 2,
      "abril": 3,
      "mayo": 4,
      "junio": 5,
      "julio": 6,
      "agosto": 7,
      "septiembre": 8,
      "octubre": 9,
      "noviembre": 10,
      "diciembre": 11
    };
    const [day, monthName, year] = inputDate.split(" de ");
    const monthIndex = monthIndexMap[monthName.toLowerCase()];
    return `${day} ${months[monthIndex]} ${year}`;
  }


  handleSelect(event: any) {
    this.router.navigateByUrl(`/blog/${event.value._id}`);
    this.goTo('nav');
    this.model = '';
  }

  getImageSrc(entry: any): string {
    const img: any = entry?.image;
    let src: string;
    if (Array.isArray(img) && img.length > 0) {
      src = img[0];
    } else if (typeof img === 'string') {
      src = img;
    } else {
      src = 'assets/images/placeholders/why-constructora-desktop.svg';
    }
    if (!src.startsWith('http') && !src.startsWith('/')) {
      src = '/' + src;
    }
    return src;
  }

  ctaGoTo(path: string) {
    this.router.navigate([path]);
  }

}
